import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { BarChart3, ClipboardList, FileText, HeartPulse, Stethoscope } from "lucide-react";
import { DashboardLayout } from "@/pages/Dashboard";
import TableWithSearch from "@/components/TableWithSearch";
import { useGetData } from "@/hooks/useGetData";
import { formatDate, formatPatientListStatus, getPatientContextTimezoneDisplay, formatOrganizationTimezoneDisplay } from "@/utils/helpers";
import { patientTableHeaders } from "@/utils/tablesData";
import { useOrg } from "@/contexts/useOrg";
import { useAuth } from "@/contexts/use-auth";
import { LegacyNursingNotesUpload } from "../components/LegacyNursingNotesUpload";

export default function LegacyDashboard() {
  const navigate = useNavigate();
  const { user , profile } = useAuth();
  const { selectedOrgId: globalSelectedOrgId } = useOrg();
  const [patients, setPatients] = useState([]);
  const [filters, setFilters] = useState({
    // status: ["active", "inactive", "newly_added"],
  });
  const { apiData: organizationsData } = useGetData(
    "/organizations",
    { limit: 100 },
    false,
    true,
  );
  const organizations = organizationsData?.data?.organizations || [];
  const isPatientUser = user?.role === "patient";
  const patientProfileId = String(profile?._id ?? "");
  const tableOrgId = globalSelectedOrgId === "anthro-care" ? "" : globalSelectedOrgId;
  const navigateToLegacyPage = (path: string, row: any) => {
    navigate(path, { state: { legacyPatient: row } });
  };
  const resolveOrganizationMongoId = (row: any) => {
    const org = row?.organizationId;
    if (org && typeof org === "object") return org._id ?? org.id;
    if (typeof org === "string" && org.trim()) return org.trim();
    return null;
  };
  const legacyOrganizationClickCell = useMemo(
    () => [
      {
        headerKey: "organizationName",
        isClickable: (row: any) => Boolean(resolveOrganizationMongoId(row)),
        onClick: (row: any) => {
          const orgId = resolveOrganizationMongoId(row);
          if (!orgId) return;
          navigate(`/organizations/${orgId}`, { state: { from: "/legacy" } });
        },
      },
    ],
    [navigate],
  );
  const actions = useMemo(
    () => [
      {
        label: "Care Plan",
        icon: <ClipboardList size={16} />,
        isShow: () => true,
        onClick: (row: any) =>
          navigateToLegacyPage(`/legacy/care-plan/${row._id}`, row),
      },
      {
        label: "Treatment Plan",
        icon: <Stethoscope size={16} />,
        isShow: () => true,
        onClick: (row: any) =>
          navigateToLegacyPage(`/legacy/treatment-plan/${row._id}`, row),
      },
      {
        label: "Nursing Notes",
        icon: <FileText size={16} />,
        isShow: () => true,
        onClick: (row: any) =>
          navigateToLegacyPage(`/legacy/nursing-notes/${row._id}`, row),
      },
      {
        label: "Readings",
        icon: <HeartPulse size={16} />,
        isShow: () => true,
        onClick: (row: any) =>
          navigateToLegacyPage(`/legacy/readings/${row._id}`, row),
      },
      {
        label: "Reading Count",
        icon: <BarChart3 size={16} />,
        isShow: () => true,
        onClick: (row: any) =>
          navigateToLegacyPage(`/legacy/reading-count/${row._id}`, row),
      },
    ],
    [navigate],
  );
  const filterOptions = useMemo(
    () => [
      // {
      //   label: "Status",
      //   key: "status",
      //   isMulti: true,
      //   values: [
      //     { label: "Active", key: "active" },
      //     { label: "Inactive", key: "inactive" },
      //     { label: "Newly Added", key: "newly_added" },
      //     { label: "Archived", key: "archived" },
      //     { label: "Opt Out", key: "denied" },
      //     { label: "Blocked", key: "blocked" },
      //     { label: "Non Compliant", key: "non_compliant" },
      //   ],
      // },
      {
        label: "Patient Type",
        key: "patientType",
        isMulti: false,
        values: [
          { label: "All", key: "all" },
          { label: "Outpatient", key: "outpatient" },
          { label: "Facility", key: "facility" },
        ],
      },
    ],
    [],
  );
  const legacyPatientTableHeaders = useMemo(
    () =>
      patientTableHeaders.map((header) =>
        header.id === "organizationId"
          ? { ...header, id: "organizationName" }
          : header,
      ),
    [],
  );

  return (
    <DashboardLayout
      activePage="/legacy"
      rightcomponent={
        user?.role === "admin" ? (
          <LegacyNursingNotesUpload organizations={organizations} />
        ) : null
      }
    >
      <div className="space-y-4">
        {/* <div>
          <h1 className="text-2xl font-semibold text-[#111827]">Legacy</h1>
          <p className="text-sm text-muted-foreground">
            Search legacy patient records and open historical program details.
          </p>
        </div> */}
        <TableWithSearch
          key={`legacy-patients-${tableOrgId || "all"}`}
          heading="Past Medical Records Patients"
          url="/patients/with-legacy-data"
          params={{
            ...(isPatientUser ? { patientId: patientProfileId } : { orgId: tableOrgId }),
            // enrollmentStatus: "enrolled",
            refreshKey: 0,
          }}
          apiDataKey="patients"
          fetchCondition={isPatientUser ? !!patientProfileId : globalSelectedOrgId !== null}
          universalLoadingSkeleton
          isIncludeSearch={!isPatientUser}
          searchPlaceholder="Search with patient name, ID, email, phone no..."
          columnHeaders={legacyPatientTableHeaders}
          data={patients}
          viewData={patients.map((v: any) => ({
            ...v,
            status: formatPatientListStatus(v?.status),
            dateOfBirth: formatDate(v?.dateOfBirth),
            firstName: `${v?.firstName || ""} ${v?.middleName || ""} ${v?.lastName || ""}`,
            organizationName: v?.organizationId?.organizationName,
            facility:
              (typeof v?.facilityId === "object" && v?.facilityId?.name) ||
              v?.facilityId ||
              "N/A",
            assignedProviderId:
              `${v?.assignedProviderId?.firstName || ""} ${v?.assignedProviderId?.middleName || ""} ${v?.assignedProviderId?.lastName || ""}`,
            programs: v?.programs?.map((p: any) => p?.toUpperCase()).join(", "),
            assignedCaretakerIds: v?.assignedCaretakerIds
              ?.map(
                (c: any) =>
                  `${c?.firstName || ""} ${c?.middleName || ""} ${c?.lastName || ""}`,
              )
              .join(", "),
            heightValue: v?.heightValue?.includes("_")
              ? `${v.heightValue.split("_")[0]}' ${v.heightValue.split("_")[1]}"`
              : v?.heightValue,
            patientTimezoneDisplay:
              formatOrganizationTimezoneDisplay(v?.resolvedTimezone) ||
              getPatientContextTimezoneDisplay(v),
            ...(v?.caretakerMinutes ? v?.caretakerMinutes : {}),
          }))}
          setData={setPatients}
          isActionColumn={false}
          filtersOptions={isPatientUser ? [] : filterOptions}
          handleAction={() => {}}
          filters={filters}
          setFilters={setFilters}
          defaultFilters={{
            status: ["active", "inactive", "newly_added"],
          }}
          isHideClearAllFilters
          clickRow={() => {}}
          clickCell={legacyOrganizationClickCell}
          moreActions={actions}
          isColumnSelection
          isIgnoreSearchParams
        />
      </div>
    </DashboardLayout>
  );
}
